import React, { useState, useEffect, useRef } from 'react';
import CandidateMetrics from '@/components/dashboard/CandidateMetrics';
import UniversityAnalytics from '@/components/dashboard/UniversityAnalytics';
import LocationAnalytics from '@/components/dashboard/LocationAnalytics';
import LanguageAnalytics from '@/components/dashboard/LanguageAnalytics';
import GenericTableAnalytics from '@/components/dashboard/GenericTableAnalytics';
import DynamicVisualization from '@/components/dashboard/DynamicVisualization';
import PieChart from '@/components/dashboard/PieChart';
import LineGraph from '@/components/dashboard/LineGraph';
import dashboardService from '@/services/dashboardService';
import { campaignsService } from '@/services/campaignsService';
import { stageService } from '@/services/stageService';
import { vacancyService } from '@/services/vacancyService';
import Loader from '@/components/Loader';
import Cookies from 'js-cookie';
import Dropdown from '@/components/ui/Dropdown';
import { Building2, Briefcase, GitBranch, XCircle, RefreshCw, Loader2, BarChart2 } from 'lucide-react';
import { generateSeriesColors } from '@/utils/helpers';
import useAuth from '@/hooks/stores/useAuth';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useNavigate, useParams } from 'react-router-dom';
// import { useCompanyBySlug } from '@/hooks/queries/useCompanies';
import useToastStore from '@/components/Common/Toast/toastStore';
import { FieldStringOutlined } from '@ant-design/icons';


const CompanyDashboard = () => {
  const [filters, setFilters] = useState({
    campaigns: [],
    vacancies: [],
    stages: [],
  });
  const [dashboardData, setDashboardData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isUniversityEnabled, setIsUniversityEnabled] = useState(false);
  const [isLanguageEnabled, setIsLanguageEnabled] = useState(false);
  const [isLocationEnabled, setIsLocationEnabled] = useState(false);
  const [isStageFilterEnabled, setIsStageFilterEnabled] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsResponse, setCampaignsResponse] = useState(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [selectedVacancies, setSelectedVacancies] = useState([]);
  const [stages, setStages] = useState([]);
  const [allStagesById, setAllStagesById] = useState([]); // Store all stages before name deduplication
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedRejectionReasons, setSelectedRejectionReasons] = useState([]);
  const [company, setCompany] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const { userRole } = useAuth();
  const pdfContentRef = useRef(null);
  const auth = useAuth();
  const navigate = useNavigate();
  const { companySlug } = useParams();
  const { showToast } = useToastStore;

  // URL validation and redirection logic
  const rawData = useAuth((state) => state.getRawData());
  const userCompanySlug = rawData?.user?.company_logins?.[0]?.company_slug;
  
  // Fetch company data by slug for validation
  // const { data: companyBySlugData, isLoading: isCompanySlugLoading } = useCompanyBySlug(companySlug);
  
  useEffect(() => {
    if(userRole?.toLowerCase() == 'company'){
      localStorage.removeItem('selectedViewCompany');
    }
    // If there's a companySlug in the URL and it doesn't match the user's company slug
    if (companySlug && userCompanySlug && companySlug !== userCompanySlug) {
      // Redirect to the correct company slug
      navigate(`/dashboard/${userCompanySlug}`, { replace: true });
     
      return;
    }
    
    // If there's no companySlug in the URL but user has a company slug, redirect to it
    if (!companySlug && userCompanySlug) {
      navigate(`/dashboard/${userCompanySlug}`, { replace: true });
      
      return;
    }
  }, [companySlug, userCompanySlug, navigate, showToast]);

  // PDF Export function
  const exportToPDF = async () => {
    if (!pdfContentRef.current) return;
    
    setIsExporting(true);
    try {
      const element = pdfContentRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('company-dashboard.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleLogout = () => {
    auth.clearTokens();
    navigate('/login');
  };

  
  // const rawData = useAuth((state) => state.getRawData());//auth.companies;

  const fetchAndSetDashboardData = async (companyId, showDataLoader = false) => {
    if (companyId) {
      if (showDataLoader) {
        setIsDataLoading(true);
      }
      // Expand selected stage names to include all IDs with the same name
      const expandedStageIds = selectedStages.length > 0 ? (() => {
        const expandedIds = [];
        selectedStages.forEach(selectedStage => {
          // First try to find in stages (deduplicated), if not found, check allStagesById
          let stageName = stages.find(s => s.id === selectedStage.value)?.name?.trim()?.toLowerCase();
          if (!stageName) {
            // If not found in stages, check allStagesById (might be a different ID with same name)
            const stageFromAll = allStagesById.find(s => s.id === selectedStage.value);
            stageName = stageFromAll?.name?.trim()?.toLowerCase();
          }
          
          if (stageName) {
            const matchingStages = allStagesById.filter(s => 
              s.name?.trim()?.toLowerCase() === stageName
            );
            expandedIds.push(...matchingStages.map(s => s.id));
          } else {
            expandedIds.push(selectedStage.value);
          }
        });
        return [...new Set(expandedIds)]; // Remove duplicates
      })() : [];

      // Check if any selected stage is a rejected stage
      const isRejectedStage = selectedStages.some(selectedStage => {
        // Check in stages first (deduplicated)
        let stageName = stages.find(s => s.id === selectedStage.value)?.name?.trim()?.toLowerCase();
        if (!stageName) {
          // If not found, check in allStagesById
          const stageFromAll = allStagesById.find(s => s.id === selectedStage.value);
          stageName = stageFromAll?.name?.trim()?.toLowerCase();
        }
        return stageName?.includes('rejected');
      });

      console.log("rejected reason", selectedRejectionReasons);
      const [dashboardResponse, campaignsResponse] = await Promise.all([
        dashboardService.getDashboard(
          companyId,
          selectedCampaigns.map(campaign => campaign.value),
          selectedVacancies.map(vacancy => vacancy.value),
          expandedStageIds,
          selectedRejectionReasons.map(rejection => rejection.vacancy_status_id)
        ),
        campaignsService.getCampaignByCompanyId(companyId)
      ]);

      // Add logs back for debugging
   

      setDashboardData(dashboardResponse);
      setCampaigns(campaignsResponse.campaigns || []);
      setCampaignsResponse(campaignsResponse);
      
      // Console log dashboard fields data for debugging
      //console.log('Dashboard Fields Data:', dashboardResponse.dashboard_fields);
  
      setCompany(campaignsResponse?.campaigns?.[0]?.company);
      
      // Check if stage filter is enabled in extra_settings
      const stageFilterSetting = dashboardResponse.dashboard_fields?.[0]?.extra_settings?.find(
        setting => setting.stage_filter !== undefined
      );
      setIsStageFilterEnabled(stageFilterSetting?.stage_filter || false);
      
      // Check if University Attendant field is enabled in dashboard fields
      const universityField = dashboardResponse.dashboard_fields?.[0]?.fields?.find(
        field => field.label === 'University attended'
      );
      const languageField = dashboardResponse.dashboard_fields?.[0]?.fields?.find(
        field => field.label === 'Languages spoken'
      );
      // const locationField = data.dashboard_fields?.[0]?.fields?.find(
      //   field => field.label === 'Phone Number'
      // );
      setIsUniversityEnabled(universityField?.isChecked || false);
      setIsLanguageEnabled(languageField?.isChecked || false);
      
      if (showDataLoader) {
        setIsDataLoading(false);
      }
   
    } else {
      console.error('Company ID is undefined or null');
    }
  }

  // Initial load effect
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const selectedViewCompany = localStorage.getItem('selectedViewCompany');
        // console.log('userRole', userRole);
        if(selectedViewCompany && userRole?.toLowerCase() == 'admin'){
          const companyData = JSON.parse(selectedViewCompany);
          await fetchAndSetDashboardData(companyData.id);
        }else if (rawData) {
          try {
            const companyId = rawData?.user?.company_logins?.[0]?.company_id;
            await fetchAndSetDashboardData(companyId);
            
          } catch (parseError) {
            console.error('Error parsing raw data:', parseError);
          }
        } else {
          console.error('No raw data found in cookies');
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []); // Only run on initial mount

  // Filter change effect
  useEffect(() => {
    const fetchFilteredData = async () => {
      // Skip if this is the initial load (campaigns not loaded yet)
      if (campaigns.length === 0) return;
      
      try {
        const selectedViewCompany = localStorage.getItem('selectedViewCompany');
        if(selectedViewCompany && userRole?.toLowerCase() == 'admin'){
          const companyData = JSON.parse(selectedViewCompany);
          await fetchAndSetDashboardData(companyData.id, true); // Show data loader
        }else if (rawData) {
          try {
            const companyId = rawData?.user?.company_logins?.[0]?.company_id;
            await fetchAndSetDashboardData(companyId, true); // Show data loader
            
          } catch (parseError) {
            console.error('Error parsing raw data:', parseError);
          }
        }
      } catch (error) {
        console.error('Error fetching filtered dashboard data:', error);
        setIsDataLoading(false);
      }
    };

    fetchFilteredData();
  }, [selectedCampaigns, selectedVacancies, selectedStages, selectedRejectionReasons]);

  const handleFilterChange = (type, value) => {
    setFilters(prev => ({
      ...prev,
      [type]: value
    }));
  };

  useEffect(() => {
    // fetch  vacancies, stages
    const fetchVacancies = async () => {
      const allStages = [];
      if(selectedCampaigns.length > 0){
        // fetch vacancies
        const vacanciesResponse = await vacancyService.getVacancies(company.id, { campaign_id: selectedCampaigns.map(campaign => campaign.value) });
        const vacancies = Array.isArray(vacanciesResponse?.data?.vacancies)
          ? vacanciesResponse.data.vacancies
          : Array.isArray(vacanciesResponse?.vacancies)
            ? vacanciesResponse.vacancies
            : Array.isArray(vacanciesResponse)
              ? vacanciesResponse
              : [];
        // if vacancies are found, set vacancies and fetch stages
        if(vacancies){
          // set vacancies
          setVacancies(vacancies);
          for (const vacancy of vacancies) {
            // fetch stages
            const stagesResponse = await stageService.getVacancyStages(vacancy.id);
            if (stagesResponse) {
              allStages.push(...stagesResponse);
            }
          }
        }
        // if vacancies are not found, set vacancies to empty array
        else{
          setVacancies([]);
        }
        // remove duplicate stages based on id
        const uniqueByIdStages = allStages.filter((stage, index, self) => 
          index === self.findIndex(s => s.id === stage.id)
        );
        // Store all stages (before name deduplication) for ID expansion
        setAllStagesById(uniqueByIdStages);
        // Then deduplicate by name (case-insensitive), keeping the first occurrence for display
        const uniqueStages = uniqueByIdStages.filter((stage, index, self) => 
          index === self.findIndex(s => s.name?.trim()?.toLowerCase() === stage.name?.trim()?.toLowerCase())
        );
        setStages(uniqueStages);
      }
      // if campaigns are not selected, set vacancies and stages to empty array
      else{
        setVacancies([]);
        setStages([]);
        setAllStagesById([]);
      }
    }
    fetchVacancies();
  }, [selectedCampaigns]);

  const handleCampaignChange = async (selectedValues) => {
    setSelectedCampaigns(selectedValues || []);
    console.log('Selected Campaign IDs:', selectedValues?.map(v => v.value) || []);
    
    // Reset vacancy and stage selections when campaigns change
    setSelectedVacancies([]);
    setSelectedStages([]);
    setSelectedRejectionReasons([]);
    
    // if (selectedValues && selectedValues.length > 0) {
    //   // Get all vacancies from selected campaigns
    //   // const allVacancies = [];
    //   // selectedValues.forEach(campaign => {
    //   //   const campaignVacancies = campaigns.find(c => c.id === campaign.value)?.vacancies || [];
    //   //   allVacancies.push(...campaignVacancies);
    //   // });
    //   // setVacancies(allVacancies);
      
    //   // Also fetch all stages from all campaigns for better UX
    //   try {
    //     const allStages = [];
    //     for (const campaign of selectedValues) {
    //       const campaignData = campaigns.find(c => c.id === campaign.value);
    //       if (campaignData?.vacancies) {
    //         for (const vacancy of campaignData.vacancies) {
    //           const stagesResponse = await stageService.getVacancyStages(vacancy.id);
    //           if (stagesResponse) {
    //             allStages.push(...stagesResponse);
    //           }
    //         }
    //       }
    //     }
    //     // Remove duplicate stages based on id
    //     const uniqueStages = allStages.filter((stage, index, self) => 
    //       index === self.findIndex(s => s.id === stage.id)
    //     );
    //     setStages(uniqueStages);
    //   } catch (error) {
    //     console.error('Error fetching campaign stages:', error);
    //     setStages([]);
    //   }
    // } else {
    //   setVacancies([]);
    //   setStages([]);
    // }
  };

  const handleVacancyChange = async (selectedValues) => {
    setSelectedVacancies(selectedValues || []);
    console.log('Selected Vacancy IDs:', selectedValues?.map(v => v.value) || []);
    
    // Reset stage selections when vacancies change
    setSelectedStages([]);
    setSelectedRejectionReasons([]);
    
    if (selectedValues && selectedValues.length > 0) {
      try {
        // Get stages for all selected vacancies using the API
        const allStages = [];
        for (const vacancy of selectedValues) {
          const stagesResponse = await stageService.getVacancyStages(vacancy.value);
          if (stagesResponse) {
            allStages.push(...stagesResponse);
          }
        }
        // Remove duplicate stages based on id first
        const uniqueByIdStages = allStages.filter((stage, index, self) => 
          index === self.findIndex(s => s.id === stage.id)
        );
        // Store all stages (before name deduplication) for ID expansion
        setAllStagesById(uniqueByIdStages);
        // Then deduplicate by name (case-insensitive), keeping the first occurrence for display
        const uniqueStages = uniqueByIdStages.filter((stage, index, self) => 
          index === self.findIndex(s => s.name?.trim()?.toLowerCase() === stage.name?.trim()?.toLowerCase())
        );
        setStages(uniqueStages);
      } catch (error) {
        console.error('Error fetching vacancy stages:', error);
        setStages([]);
        setAllStagesById([]);
      }
    } else if (selectedCampaigns.length > 0) {
      // If no vacancies selected but campaigns are selected, show all stages from all campaigns
      try {
        const allStages = [];
        for (const campaign of selectedCampaigns) {
          const campaignData = campaigns.find(c => c.id === campaign.value);
          if (campaignData?.vacancies) {
            for (const vacancy of campaignData.vacancies) {
              const stagesResponse = await stageService.getVacancyStages(vacancy.id);
              if (stagesResponse) {
                allStages.push(...stagesResponse);
              }
            }
          }
        }
        // Remove duplicate stages based on id first
        const uniqueByIdStages = allStages.filter((stage, index, self) => 
          index === self.findIndex(s => s.id === stage.id)
        );
        // Store all stages (before name deduplication) for ID expansion
        setAllStagesById(uniqueByIdStages);
        // Then deduplicate by name (case-insensitive), keeping the first occurrence for display
        const uniqueStages = uniqueByIdStages.filter((stage, index, self) => 
          index === self.findIndex(s => s.name?.trim()?.toLowerCase() === stage.name?.trim()?.toLowerCase())
        );
        setStages(uniqueStages);
      } catch (error) {
        console.error('Error fetching all stages:', error);
        setStages([]);
        setAllStagesById([]);
      }
    } else {
      setStages([]);
      setAllStagesById([]);
    }
  };

  // Helper function to expand selected stage names to all matching IDs
  const expandStageIds = (selectedStageValues) => {
    if (!selectedStageValues || selectedStageValues.length === 0) return [];
    
    const valuesArray = Array.isArray(selectedStageValues) ? selectedStageValues : [selectedStageValues];
    const expandedIds = [];
    
    valuesArray.forEach(selectedStage => {
      const selectedStageObj = typeof selectedStage === 'object' ? selectedStage : { value: selectedStage };
      // First try to find in stages (deduplicated), if not found, check allStagesById
      let stageName = stages.find(s => s.id === selectedStageObj.value)?.name?.trim()?.toLowerCase();
      if (!stageName) {
        // If not found in stages, check allStagesById (might be a different ID with same name)
        const stageFromAll = allStagesById.find(s => s.id === selectedStageObj.value);
        stageName = stageFromAll?.name?.trim()?.toLowerCase();
      }
      
      if (stageName) {
        const matchingStages = allStagesById.filter(s => 
          s.name?.trim()?.toLowerCase() === stageName
        );
        expandedIds.push(...matchingStages.map(s => s.id));
      } else {
        // If stage not found in either, use the value directly
        expandedIds.push(selectedStageObj.value);
      }
    });
    
    return [...new Set(expandedIds)]; // Remove duplicates
  };

  const handleStageChange = (selectedValues) => {
    // Ensure selectedValues is always an array
    const valuesArray = selectedValues ? (Array.isArray(selectedValues) ? selectedValues : [selectedValues]) : [];
    setSelectedStages(valuesArray);
    setSelectedRejectionReasons([]); // Reset rejection reasons when stages change
    
    const expandedIds = expandStageIds(valuesArray);
    console.log('Selected Stage Names:', valuesArray.map(v => stages.find(s => s.id === v.value)?.name));
    console.log('Expanded Stage IDs:', expandedIds);
  };

  const handleRejectionReasonChange = (selectedValue) => {
    // Handle both single value (when multiSelect=false) and array (when multiSelect=true)
    const values = selectedValue ? [selectedValue] : [];
    setSelectedRejectionReasons(values);
    
    console.log('Selected Rejection Stage IDs:', values.map(v => v.value));
  };

  // Calculate chart pairs and listing field pairs for rendering
  const fieldsWithVisualization = dashboardData?.dashboard_fields?.[0]?.fields
    ?.filter(field => field.isChecked && field.visualization_type) || [];
  
  // Separate listing fields from other visualization types
  const listingFields = fieldsWithVisualization.filter(field => 
    field.visualization_type && field.visualization_type.startsWith('listing__')
  );
  const otherFields = fieldsWithVisualization.filter(field => 
    !field.visualization_type || !field.visualization_type.startsWith('listing__')
  );
  
  // Separate pie charts from other charts
  const pieChartFields = otherFields.filter(field => field.visualization_type === 'pie');
  const nonPieChartFields = otherFields.filter(field => field.visualization_type !== 'pie' && field.visualization_type !== 'card');
  
  // Group pie charts with other charts in pairs
  const chartPairs = [];
  let pieIndex = 0;
  let nonPieIndex = 0;
  
  while (pieIndex < pieChartFields.length) {
    const pair = [pieChartFields[pieIndex]];
    
    // Add a non-pie chart if available
    if (nonPieIndex < nonPieChartFields.length) {
      pair.push(nonPieChartFields[nonPieIndex]);
      nonPieIndex++;
    }
    
    chartPairs.push(pair);
    pieIndex++;
  }
  
  // Add remaining non-pie charts as individual items
  while (nonPieIndex < nonPieChartFields.length) {
    chartPairs.push([nonPieChartFields[nonPieIndex]]);
    nonPieIndex++;
  }

  // Group listing fields into pairs (keeping original design)
  const listingFieldPairs = [];
  for (let i = 0; i < listingFields.length; i += 2) {
    listingFieldPairs.push(listingFields.slice(i, i + 2));
  }
  
  // Recursive function to check if any field in nested array has data
  function hasListingData(fieldPairs, fieldData) {
    for (const item of fieldPairs) {
      if (Array.isArray(item)) {
        if (hasListingData(item, fieldData)) return true;
      } else if (fieldData?.[item.label]?.length > 0) {
        return true;
      }
    }
    return false;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-background">
        <Loader bgColor="transparent" loadingText="Loading dashboard..." />
      </div>
    );
  }

  

  return (
    <div ref={pdfContentRef} className="p-6">
      <div className="mb-6" >
        <div className="flex items-center gap-2 justify-between">
          <h1 className="text-2xl font-bold mb-4">Company Analytics Dashboard</h1>
          <div className="flex items-center gap-2">
          
            <button
              type="button"
              className={`py-2 px-4 -mt-4 rounded-lg text-white focus:outline-none flex items-center gap-2 ${
                !company?.brand_color ? 'bg-primary hover:bg-primary-dark' : ''
              } ${
                isExporting 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:opacity-80 cursor-pointer'
              }`}
              style={company?.brand_color ? { backgroundColor: company.brand_color } : {}}
              title={isExporting ? "Exporting..." : "Export PDF"}
              onClick={exportToPDF}
              disabled={isExporting}
              data-html2canvas-ignore="true"
            >
              {isExporting ? (
                <>
                  <span>Export PDF</span>
                </>
              ) : (
                'Export PDF'
              )}
            </button>
          </div>
        </div>
        <div className="flex flex-wrap justify-start gap-4 bg-card p-4 rounded-lg" data-html2canvas-ignore="true">
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-foreground mb-1">Campaign</label>
            <div className="flex items-center gap-2">
              <Dropdown
                options={[
                  ...campaigns?.map(campaign => ({
                    label: campaign.title,
                    value: campaign.id
                  }))
                ]}
                selectedValues={selectedCampaigns}
                onChange={handleCampaignChange}
                placeholder="Select Campaigns"
                icon={<Building2 className="w-5 h-5 text-foreground flex-shrink-0" />}
                searchable={true}
                multiSelect={true}
                className="min-h-[50px] w-auto min-w-[260px] border border-border rounded-lg"
                dropdownWidth="w-auto min-w-[30%]"
                renderOption={(option) => (
                  <p className="text-foreground">
                    {option.label}
                  </p>
                )}
                allowAll={false}
              />
              {selectedCampaigns.length > 0 && (
                <button
                  type="button"
                  className="p-1 rounded hover:bg-muted focus:outline-none"
                  onClick={() => {
                    setSelectedCampaigns([]);
                    setSelectedVacancies([]);
                    setSelectedStages([]);
                    setSelectedRejectionReasons([]);
                    setVacancies([]);
                    setStages([]);
                  }}
                  title="Reset Campaign Filter"
                >
                  <RefreshCw className="w-5 h-5 text-foreground" />
                </button>
              )}
            </div>
          </div>
          {selectedCampaigns.length > 0 && (
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-foreground mb-1">Vacancy</label>
              <div className="flex items-center gap-2">
                <Dropdown
                  options={vacancies.length > 0 ? [
                    ...vacancies?.map(vacancy => ({
                      label: vacancy.title,
                      value: vacancy.id
                    }))
                  ] : [{
                    label: "No Vacancies",
                    value: "no_vacancies",
                    disabled: true
                  }]}
                  selectedValues={selectedVacancies}
                  onChange={handleVacancyChange}
                  placeholder={vacancies.length > 0 ? "Select Vacancies" : "No Vacancies"}
                  icon={<Briefcase className="w-5 h-5 text-foreground flex-shrink-0" />}
                  searchable={true}
                  multiSelect={true}
                  className="w-auto min-w-[260px] border border-border rounded-lg"
                  dropdownWidth="w-auto min-w-[30%]"
                  renderOption={(option) => (
                    <p className={`text-foreground ${option.disabled ? 'opacity-50' : ''}`}>
                      {option.label}
                    </p>
                  )}
                  allowAll={false}
                  disabled={vacancies.length === 0}
                />
                {selectedVacancies.length > 0 && (
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-muted focus:outline-none"
                    onClick={() => {
                      setSelectedVacancies([]);
                      setSelectedStages([]);
                      setSelectedRejectionReasons([]);
                      setStages([]);
                    }}
                    title="Reset Vacancy Filter"
                  >
                    <RefreshCw className="w-5 h-5 text-foreground" />
                  </button>
                )}
              </div>  
            </div>
          )}
          {selectedVacancies.length > 0 && (
            <>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-foreground mb-1">Stage</label>
                <div className="flex items-center gap-2">
                  <Dropdown
                    options={stages.length > 0 ? [
                      ...stages?.map(stage => ({
                        label: stage.name,
                        value: stage.id
                      }))
                    ] : [{
                      label: "No Stages",
                      value: "no_stages",
                      disabled: true
                    }]}
                    selectedValues={selectedStages}
                    onChange={handleStageChange}
                    placeholder={stages.length > 0 ? "Select Stages" : "No Stages"}
                    icon={<GitBranch className="w-5 h-5 text-foreground flex-shrink-0" />}
                    searchable={true}
                    multiSelect={selectedVacancies.length == 1 ? true : false}
                    className="w-auto min-w-[260px] border border-border rounded-lg"
                    dropdownWidth="w-auto min-w-[30%]"
                    renderOption={(option) => (
                      <p className={`text-foreground ${option.disabled ? 'opacity-50' : ''}`}>
                        {option.label}
                      </p>
                    )}
                    allowAll={false}
                    disabled={stages.length === 0}
                  />
                  {selectedStages.length > 0 && (
                    <button
                      type="button"
                      className="p-1 rounded hover:bg-muted focus:outline-none"
                      onClick={() => {
                        setSelectedStages([]);
                        setSelectedRejectionReasons([]);
                      }}
                      title="Reset Stage Filter"
                    >
                      <RefreshCw className="w-5 h-5 text-foreground" />
                    </button>
                  )}
                </div>
              </div>
              {selectedStages.some(selectedStage => {
                // Check in stages first (deduplicated)
                let stageName = stages.find(s => s.id === selectedStage.value)?.name?.trim()?.toLowerCase();
                if (!stageName) {
                  // If not found, check in allStagesById (might be a different ID with same name)
                  const stageFromAll = allStagesById.find(s => s.id === selectedStage.value);
                  stageName = stageFromAll?.name?.trim()?.toLowerCase();
                }
                return stageName === 'rejected';
              }) && (
                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-foreground mb-1">Rejected at </label>
                  <div className="flex items-center gap-2">
                    <Dropdown
                      options={stages.length > 0 ? [
                        ...stages?.filter(stage => stage.name?.trim()?.toLowerCase() !== 'rejected').map(stage => ({
                          label: stage.name,
                          value: stage.id,
                          vacancy_status_id: stage.vacancy_status_id,
                        }))
                      ] : [{
                        label: "No Stages",
                        value: "no_stages",
                        vacancy_status_id: null,
                        disabled: true
                      }]}
                      value={selectedRejectionReasons.length > 0 ? selectedRejectionReasons[0] : null}
                      onChange={handleRejectionReasonChange}
                      placeholder={stages.length > 0 ? "Select Stage" : "No Stages"}
                      icon={<GitBranch className="w-5 h-5 text-foreground flex-shrink-0" />}
                      searchable={true}
                      multiSelect={false}
                      className="w-auto min-w-[260px] border border-border rounded-lg"
                      dropdownWidth="w-auto min-w-[30%]"
                      renderOption={(option) => (
                        <p className={`text-foreground ${option.disabled ? 'opacity-50' : ''}`}>
                          {option.label}
                        </p>
                      )}
                      allowAll={false}
                      disabled={stages.length === 0}
                    />
                    {selectedRejectionReasons.length > 0 && (
                      <button
                        type="button"
                        className="p-1 rounded hover:bg-muted focus:outline-none"
                        onClick={() => {
                          setSelectedRejectionReasons([]);
                        }}
                        title="Reset Rejection Reason Filter"
                      >
                        <RefreshCw className="w-5 h-5 text-foreground " />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Single Loader for all analytics sections */}
      {isDataLoading && (
        <div className="fixed inset-0 bg-background/75 z-50 flex items-center justify-center">
          <Loader bgColor="transparent" loadingText="Updating dashboard..." />
        </div>
      )}

      {/* Main Metrics */}
      <CandidateMetrics dashboardData={dashboardData} />

      {/* Dynamic Visualizations based on field settings */}
      <div className="mt-6">
        <>
          {/* Render chart pairs (pie charts with other charts) */}
          {company && chartPairs.map((fieldPair, pairIndex) => (
            <div key={`chart-pair-${pairIndex}`} className="mb-6">
              {fieldPair.length === 1 ? (
                // Single chart - check if it's a pie chart
                fieldPair[0].visualization_type === 'pie' ? (
                  // Single pie chart - center with proper styling
                  <div className="flex justify-center">
                    <div className="w-1/2">
                      <DynamicVisualization 
                        seriesColors={generateSeriesColors(dashboardData?.field_data?.[fieldPair[0].label]?.length, company?.brand_color)} 
                        company={company} 
                        field={fieldPair[0]} 
                        dashboardData={dashboardData} 
                      />
                    </div>
                  </div>
                ) : (
                  // Single non-pie chart - full width
                  <DynamicVisualization 
                    seriesColors={generateSeriesColors(dashboardData?.field_data?.[fieldPair[0].label]?.length, company?.brand_color)} 
                    company={company} 
                    field={fieldPair[0]} 
                    dashboardData={dashboardData} 
                  />
                )
              ) : (
                // Two charts in a row
                <div className="bg-card rounded-lg shadow-sm">
                  <div className="p-4">
                    <div className="flex mb-6 gap-6">
                      {fieldPair.map((field, index) => (
                        <div key={field.id} className="flex-1">
                          <DynamicVisualization 
                            seriesColors={generateSeriesColors(dashboardData?.field_data?.[field.label]?.length, company?.brand_color)} 
                            company={company} 
                            field={field} 
                            dashboardData={dashboardData} 
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Render listing fields at the end with original design */}
          {company && hasListingData(listingFieldPairs, dashboardData?.field_data) && (
            <div className="">
              <div className="bg-card rounded-lg shadow-sm">
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 mb-6 gap-6">
                    {listingFieldPairs.map((fieldPair, pairIndex) => (
                      fieldPair.filter(field => dashboardData?.field_data?.[field.label]?.length > 0).map((field, index) => (
                        <div key={field.id} className="">
                          <DynamicVisualization seriesColors={generateSeriesColors(dashboardData?.field_data?.[field.label]?.length, company?.brand_color)} company={company} field={field} dashboardData={dashboardData} />
                        </div>
                      ))
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      </div>

      {/* Legacy Analytics Sections - keep for backward compatibility */}
      <div className=" grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* {isUniversityEnabled && (
          <div>
            <UniversityAnalytics dashboardData={dashboardData} />
          </div>
        )} */}
        {isLocationEnabled && (
          <div>
            <LocationAnalytics />
          </div>
        )}  
      </div>
  
      
      {/* Generic Table Analytics - for fields without specific visualization types */}
      <div className="">
        <GenericTableAnalytics dashboardData={dashboardData} />
      </div>

      {/* Show generic message if no analytics data is available */}
      {!(
        (company && chartPairs && chartPairs.length > 0) ||
        (company && hasListingData && hasListingData(listingFieldPairs, dashboardData?.field_data)) ||
        (dashboardData && dashboardData.field_data && Object.keys(dashboardData.field_data).length > 0)
      ) && (
        <div className="flex flex-col items-center justify-center h-96 bg-card rounded-lg shadow-sm mb-8">
          <BarChart2 className="w-12 h-12 text-muted-foreground mb-4" />
          <span className="text-muted-foreground text-xl font-semibold mb-2">No analytics data available</span>
        </div>
      )}

      {/* Move Logout button to the very bottom of the dashboard */}
      <div className="flex justify-start mt-4 ">
        <button
          type="button"
          className="py-2 px-4 rounded-lg text-white focus:outline-none transition-all duration-200 flex items-center gap-2 hover:opacity-80 cursor-pointer"
          style={{ backgroundColor: '#EF4444' }}
          title="Logout"
          onClick={handleLogout}
          data-html2canvas-ignore="true"
        >
          Logout
        </button>
      </div>
    </div>
  );
};

export default CompanyDashboard; 